import type { FragmentType } from 'src/gql';
import { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { graphql, unmaskFragment } from 'src/gql';

import { formatDollarAmount } from '../../../../utils/formatting';
import { CURRENT_TAX_YEAR } from '../tax-utils';
import { GraphTooltip } from './graph-tooltip';

export const TaxHistoryGraphFragment = graphql(/* GraphQL */ `
  fragment TaxHistoryGraphFragment on TaxPageClient {
    userFullName
    taxTranscripts {
      id
      taxYear
      totalIncome
      federalTaxLiability
      federalRefund
      totalDeductions
      standardOrItemizedDeduction
      adjustedGrossIncome
      taxableIncome
    }
  }
`);

type Props = {
  readonly clientData: FragmentType<typeof TaxHistoryGraphFragment> | undefined;
};

const CHART_COLORS = {
  totalIncome: 'var(--chart-1-dark)',
  federalTaxLiability: 'var(--chart-2-light)',
  federalRefund: 'var(--chart-3-dark)',
  totalDeductions: 'var(--chart-4-light)',
};

type CustomTooltipProps = {
  readonly active?: boolean;
  readonly payload?: {
    payload: {
      adjustedGrossIncome?: number | null;
      totalIncome?: number | null;
      totalDeductions?: number | null;
      standardOrItemizedDeduction?: number | null;
      federalTaxLiability?: number | null;
      federalRefund?: number | null;
    };
  }[];
  readonly hoveredBar: string | null;
};

function CustomTooltip({ active, payload, hoveredBar }: CustomTooltipProps) {
  if (!active || !payload?.length || !hoveredBar) return null;

  const data = payload[0]?.payload;

  switch (hoveredBar) {
    case 'totalIncome':
      return (
        <GraphTooltip
          title="Income"
          rows={[
            { label: 'Adjusted Gross Income', value: data?.adjustedGrossIncome ?? 0 },
            {
              label: 'Adjustments',
              value: Math.max(0, (data?.totalIncome ?? 0) - (data?.adjustedGrossIncome ?? 0)),
            },
          ]}
          totalLabel="Total Income"
          totalValue={data?.totalIncome ?? 0}
        />
      );

    case 'totalDeductions': {
      const totalDeductions = data?.totalDeductions ?? 0;
      const standardOrItemized = data?.standardOrItemizedDeduction ?? 0;
      const otherDeductions = Math.max(0, totalDeductions - standardOrItemized);

      return (
        <GraphTooltip
          title="Deductions"
          rows={[
            { label: 'Standard / Itemized', value: standardOrItemized },
            { label: 'Other', value: otherDeductions },
          ]}
          totalLabel="Total Deductions"
          totalValue={totalDeductions}
        />
      );
    }

    case 'federalTaxLiability': {
      const federalTax = data?.federalTaxLiability ?? 0;

      return <GraphTooltip title="Federal Tax" rows={[]} totalLabel="Federal Tax Liability" totalValue={federalTax} />;
    }

    case 'federalRefund': {
      const federalRefund = data?.federalRefund ?? 0;

      return (
        <GraphTooltip title="Federal Refund/owed" rows={[]} totalLabel="Federal Refund" totalValue={federalRefund} />
      );
    }

    default:
      return null;
  }
}

type HoveredBar = {
  readonly type: string;
  readonly id: string;
} | null;

export function TaxHistoryGraph({ clientData }: Props) {
  const data = unmaskFragment(TaxHistoryGraphFragment, clientData);
  const [hoveredBar, setHoveredBar] = useState<HoveredBar>(null);

  const chartData = useMemo(() => {
    const transcripts = data?.taxTranscripts ?? [];
    const sorted = [...transcripts].sort((a, b) => a.taxYear - b.taxYear);

    // Add current tax year placeholder if transcripts exist but current year doesn't
    const hasCurrentYear = sorted.some((t) => t.taxYear === CURRENT_TAX_YEAR);
    if (sorted.length > 0 && !hasCurrentYear) {
      sorted.push({
        id: `placeholder-${CURRENT_TAX_YEAR}`,
        taxYear: CURRENT_TAX_YEAR,
        totalIncome: null,
        federalTaxLiability: null,
        federalRefund: null,
        totalDeductions: null,
        standardOrItemizedDeduction: null,
        adjustedGrossIncome: null,
        taxableIncome: null,
      });
    }

    return sorted.map((t) => {
      const total = Number(t.totalIncome ?? 0);
      const tax = Number(t.federalTaxLiability ?? 0);
      const refund = Number(Math.abs(t.federalRefund ?? 0));
      const deductions = Number(t.totalDeductions ?? 0);

      return {
        ...t,
        year: String(t.taxYear),
        federalRefund: refund, // Normalized for Recharts
        baseIncome: Math.max(0, total - tax - refund - deductions),
      };
    });
  }, [data?.taxTranscripts]);

  if (chartData.length === 0) return null;

  const maxValue = Math.max(...chartData.map((item) => item.totalIncome ?? 0));
  const yAxisMax = Math.ceil(maxValue / 100000) * 100000 || 100000;

  return (
    <div className="w-full bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 16, right: 50, left: 10, bottom: 30 }} barCategoryGap="28.26%">
            <defs>
              {Object.entries(CHART_COLORS).map(([key, color]) => (
                <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={1} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.7} />
                </linearGradient>
              ))}
            </defs>

            <CartesianGrid strokeDasharray="0" stroke="#f1f5f9" horizontal={true} vertical={false} />
            <XAxis
              dataKey="year"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 500 }}
              dy={15}
            />
            <YAxis
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              tickFormatter={(value) => formatDollarAmount(value, false)}
              domain={[0, yAxisMax]}
              ticks={[0, yAxisMax / 3, (yAxisMax * 2) / 3, yAxisMax]}
            />

            <Tooltip
              content={<CustomTooltip hoveredBar={hoveredBar?.type ?? null} />}
              cursor={{ fill: 'transparent' }}
            />

            <Bar dataKey="federalRefund" stackId="a" stroke="white" strokeWidth={2} fill="url(#gradient-federalRefund)">
              {chartData.map((entry) => (
                <Cell
                  key={`refund-${entry.id}`}
                  opacity={
                    hoveredBar === null || (hoveredBar.type === 'federalRefund' && hoveredBar.id === entry.id) ? 1 : 0.6
                  }
                  onMouseEnter={() => setHoveredBar({ type: 'federalRefund', id: entry.id })}
                  onMouseLeave={() => setHoveredBar(null)}
                />
              ))}
            </Bar>

            <Bar
              dataKey="federalTaxLiability"
              stackId="a"
              stroke="white"
              strokeWidth={2}
              fill="url(#gradient-federalTaxLiability)"
            >
              {chartData.map((entry) => (
                <Cell
                  key={`tax-${entry.id}`}
                  opacity={
                    hoveredBar === null || (hoveredBar.type === 'federalTaxLiability' && hoveredBar.id === entry.id)
                      ? 1
                      : 0.6
                  }
                  onMouseEnter={() => setHoveredBar({ type: 'federalTaxLiability', id: entry.id })}
                  onMouseLeave={() => setHoveredBar(null)}
                />
              ))}
            </Bar>

            <Bar
              dataKey="totalDeductions"
              stackId="a"
              stroke="white"
              strokeWidth={2}
              fill="url(#gradient-totalDeductions)"
            >
              {chartData.map((entry) => (
                <Cell
                  key={`deduction-${entry.id}`}
                  opacity={
                    hoveredBar === null || (hoveredBar.type === 'totalDeductions' && hoveredBar.id === entry.id)
                      ? 1
                      : 0.6
                  }
                  onMouseEnter={() => setHoveredBar({ type: 'totalDeductions', id: entry.id })}
                  onMouseLeave={() => setHoveredBar(null)}
                />
              ))}
            </Bar>

            <Bar
              dataKey="baseIncome"
              stackId="a"
              radius={[6, 6, 0, 0]}
              stroke="white"
              strokeWidth={2}
              fill="url(#gradient-totalIncome)"
            >
              {chartData.map((entry) => (
                <Cell
                  key={`base-${entry.id}`}
                  opacity={
                    hoveredBar === null || (hoveredBar.type === 'totalIncome' && hoveredBar.id === entry.id) ? 1 : 0.6
                  }
                  onMouseEnter={() => setHoveredBar({ type: 'totalIncome', id: entry.id })}
                  onMouseLeave={() => setHoveredBar(null)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
